import { AppType } from "client/Api/Apps/common";
import {
	ReactElement,
	useCallback
} from "react";
import { useRenderManager } from "../../useRenderManager";
import { OneByOneTileContent } from "../OneByOneTileContent";
import { TwoByTwoTileContent } from "../TwoByTwoTileContent";
import { ConnectWiseLiveInformationProvider } from "client_legacy/Workspace/LiveInformation/ConnectWise/provider";
import { ConnectWiseLiveTileContent } from "./ConnectWiseLiveTileContent";
import { navigateToConnectWiseApp } from "client_legacy/Apps/appNavigation";
import { useRegisterTileUsage } from "../useRegisterTileUsage";
import { AlertManager, useAlerts } from "client/Hooks";
import { localize } from "client_legacy/ndaw/culture";
import { TileRenderState } from "../../RenderState/common";
import { useBaseNavigation } from "../useInvokeNavigation";
import { TileContextProvider } from "../../TileContextProvider";
import { Coordinate } from "client/Misc/commonTypes";

export type ConnectWiseTileContentProps = {
	renderState: TileRenderState<AppType.ConnectWise>;
	onEdit: (() => void) | undefined;
	onOpenTileMenu: (coordinate: Coordinate) => void;
	provider: ConnectWiseLiveInformationProvider;
};

export const ConnectWiseTileContent = (props: ConnectWiseTileContentProps): ReactElement => {
	const {
		renderState,
		onEdit,
		onOpenTileMenu,
		provider
	} = props;

	const [navigationElement, navigateHandler] = useConnectWiseTileNavigation(
		renderState,
		onEdit,
		provider
	);

	const { dimension } = renderState;

	const tileContent: ReactElement = useRenderManager(
		dimension,
		() => (
			<ConnectWiseLiveTileContent
				provider={provider}
				onNavigate={navigateHandler}
				onOpenTileMenu={onOpenTileMenu}
			/>
		),
		[
			{ columnSpan: 1, rowSpan: 1 },
			() => (
				<OneByOneTileContent
					onClick={navigateHandler}
					onOpenTileMenu={onOpenTileMenu}
				/>
			)
		],
		[
			{ columnSpan: 2, rowSpan: 2 },
			() => (
				<TwoByTwoTileContent
					onClick={navigateHandler}
					onOpenTileMenu={onOpenTileMenu}
				/>
			)
		]
	);

	return (
		<>
			<TileContextProvider
				renderState={renderState}
			>
				{tileContent}
			</TileContextProvider>

			{navigationElement}
		</>
	);
};
ConnectWiseTileContent.displayName = "ConnectWiseTileContent";

export function useConnectWiseTileNavigation(
	renderStateIsh: Pick<TileRenderState<AppType.ConnectWise>, "data" | "dimension" | "interactionState">,
	onEdit: (() => void) | undefined,
	provider: ConnectWiseLiveInformationProvider
): [element: ReactElement | null, () => void | Promise<void>] {
	const registerTileUsage: () => void = useRegisterTileUsage(renderStateIsh);
	const alertManager: AlertManager = useAlerts();

	const { isConnectWiseConfigured } = provider;
	const {
		environmentAppId,
		settings
	} = renderStateIsh.data;

	return useBaseNavigation(
		renderStateIsh,
		onEdit,
		useCallback(
			(): void => {
				if (!isConnectWiseConfigured) {
					alertManager.showError(localize("_IncompleteConfigurationPleaseContactYourAdmin"));
					return;
				}

				registerTileUsage();

				navigateToConnectWiseApp({
					id: environmentAppId,
					destinationWindow: settings.destinationWindow
				});
			},
			[
				alertManager,
				environmentAppId,
				isConnectWiseConfigured,
				registerTileUsage,
				settings.destinationWindow
			]
		)
	);
}
